import { useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { UnauthorizedError } from '../errors/UnauthorizedError';
import { codeyApiClient } from './codeyApiClient';

export function usePrivateApi() {
  const { user } = useAuth();

  const doGet = useCallback<GetRequest>(
    async (url, options = {}) => {
      if (!user?.token) {
        throw new UnauthorizedError('User is not authenticated');
      }

      return codeyApiClient.get(url, {
        authToken: user.token,
        headers: options.headers,
      });
    },
    [user?.token]
  );

  const doPut = useCallback<PutRequest>(
    async (url, body, options = {}) => {
      if (!user?.token) {
        throw new UnauthorizedError('User is not authenticated');
      }

      return codeyApiClient.put(url, body, {
        authToken: user.token,
        headers: options.headers,
      });
    },
    [user?.token]
  );

  const doPost = useCallback<PostRequest>(
    async (url, body, options = {}) => {
      if (!user?.token) {
        throw new UnauthorizedError('User is not authenticated');
      }

      return codeyApiClient.post(url, body, {
        authToken: user.token,
        headers: options.headers,
      });
    },
    [user?.token]
  );

  const doDelete = useCallback<DeleteRequest>(
    async (url, options = {}) => {
      if (!user?.token) {
        throw new UnauthorizedError('User is not authenticated');
      }

      return codeyApiClient.delete(url, {
        authToken: user.token,
        headers: options.headers,
      });
    },
    [user?.token]
  );

  return { doGet, doPost, doPut, doDelete };
}

type GetRequest = <Output>(url: string, options?: RequestInit) => Promise<Output>;
type PostRequest = <Input, Output>(url: string, body: Input, options?: RequestInit) => Promise<Output>;
type PutRequest = <Input, Output>(url: string, body: Input, options?: RequestInit) => Promise<Output>;
type DeleteRequest = (url: string, options?: RequestInit) => Promise<void>;
